import type { FeatureFlagKey } from '@erp/shared-types';
import { getPermissionKey } from '@/hooks/authz/useRoutePermissions';
import type { ProductModule } from '@/hooks/organization-config/useOrganizationEnabledModules';
import {
  isSectionHiddenByFlags,
  type OrganizationSettingsSection,
} from '@/pages/authenticated/settings/organization/organizationSettingsSections';

export interface QuickSearchSettingsViewer {
  /** Gently staff (admin view): opens staff sections and skips permissions. */
  gently: boolean;
  /** Holds Update OrganizationSettings: opens staff sections only. */
  canManageStaffSections: boolean;
  isModuleEnabled: (module: ProductModule) => boolean;
  permissions: Map<string, boolean>;
  isFeatureEnabled: (flag: FeatureFlagKey) => boolean;
}

function holdsRequiredPermission(
  section: OrganizationSettingsSection,
  viewer: QuickSearchSettingsViewer,
): boolean {
  const required = [
    ...(section.requiredPermission ? [section.requiredPermission] : []),
    ...(section.requiredAnyPermission ?? []),
  ];
  if (required.length === 0 || viewer.gently) return true;
  return required.some(
    ({ action, subject }) =>
      viewer.permissions.get(getPermissionKey(action, subject)) ?? false,
  );
}

/** Whether Quick Find may offer an organization-settings section. */
export function isQuickSearchSettingAccessible(
  section: OrganizationSettingsSection,
  viewer: QuickSearchSettingsViewer,
): boolean {
  const staffGate =
    !section.gentlyOnly || viewer.gently || viewer.canManageStaffSections;
  const moduleGate =
    !section.requiredModule || viewer.isModuleEnabled(section.requiredModule);
  return (
    staffGate &&
    moduleGate &&
    !isSectionHiddenByFlags(section, viewer.isFeatureEnabled) &&
    holdsRequiredPermission(section, viewer)
  );
}
